import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type BillingRateInfoJSON = z.infer<typeof BillingRateInfoSchema.BillingRateInfoJSONZ>;
export type BillingRateInfoParams = z.infer<typeof BillingRateInfoSchema.BillingRateInfoZ>;

export default class BillingRateInfoSchema {
  public static readonly BillingRateInfoJSONZ = z.object({
    billingRate: z.string(),
    billingUnit: z.string().nullish(),
  });

  public static readonly BillingRateInfoZ = z.object({
    billingRate: z.string(),
    billingUnit: z.string().optional(),
  });

  public static validate(values: unknown): BillingRateInfoParams {
    return parseOrThrow(BillingRateInfoSchema.BillingRateInfoZ, values, {
      logError: true,
      schemaName: "BillingRateInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): BillingRateInfoJSON {
    return parseOrThrow(BillingRateInfoSchema.BillingRateInfoJSONZ, json, {
      logError: true,
      schemaName: "BillingRateInfoSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is BillingRateInfoParams {
    try {
      BillingRateInfoSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is BillingRateInfoJSON {
    try {
      BillingRateInfoSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): BillingRateInfoParams {
    const parsed = parseOrThrow(BillingRateInfoSchema.BillingRateInfoJSONZ, json, {
      logError: true,
      schemaName: "BillingRateInfoSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return BillingRateInfoSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: BillingRateInfoParams): BillingRateInfoJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = BillingRateInfoSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return BillingRateInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    BillingRateInfoSchema.BillingRateInfoJSONZ,
    BillingRateInfoSchema.BillingRateInfoZ,
    {
      decode: (data) => ({
        billingRate: data.billingRate,
        billingUnit: data.billingUnit ?? undefined,
      }),
      encode: (rate) => ({
        billingRate: rate.billingRate,
        billingUnit: rate.billingUnit,
      }),
    }
  );
}
